import { datadogRum } from '@datadog/browser-rum';
import React, { ElementType, FC, memo, useEffect, useMemo } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';

import { Page } from '~/app/Layout';
import { PageHeaderBorder } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { PageType, PageTypeLevel, Sitemap } from '~/app/Sitemap';
import { FeatureFlag, FeatureFlags, useFeatureFlags } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { isAllowedAppAccess } from '~/lib/session';
import { useSessionStore } from '~/stores/Session';

import { UnauthorizedState } from './_components';

interface RouteProps {
  page: PageType<PageTypeLevel>;
  featureFlags: FeatureFlags;
  url: string;
}

const getSitemapRoute = ({ page, featureFlags, url }: RouteProps) => {
  const nestedPath = typeof page.url === 'string' ? page.url : page.url.route;
  const path = `${url}/${nestedPath}`.replace(/\/+/g, '/');

  const isFeatureEnabled = !page.rules?.requireFeatureFlag || featureFlags[page.rules?.requireFeatureFlag];

  if ('childPages' in page) {
    let routeElement;
    if (page.routeElement) {
      routeElement = (
        <Page
          element={page.routeElement}
          entries={page.childPages
            .filter((childPage) => childPage.visibility === 'tab')
            .map((childPage) => {
              const childPath =
                typeof childPage.url === 'string' ? childPage.url : childPage.url.redirect || childPage.url.route;

              return {
                label: childPage.title,
                path: `${ROUTE.ROOT}${childPath === '' ? path : `${path}/${childPath}`}`,
                removeWildcard: childPath === '',
              };
            })}
        />
      );
    }

    if (isFeatureEnabled) {
      return (
        <Route key={path} path={path} element={routeElement}>
          {page.childPages.map((childPage) => getSitemapRoute({ page: childPage, featureFlags, url: path }))}

          <Route index element={<PageRenderer page={page} />} />
        </Route>
      );
    }
    return (
      <React.Fragment key={path}>
        {page.childPages.map((childPage) => getSitemapRoute({ page: childPage, featureFlags, url: path }))}
      </React.Fragment>
    );
  }

  if (!isFeatureEnabled) {
    return null;
  }

  return <Route key={path} path={path} element={<PageRenderer page={page} />} />;
};

const PageRenderer: FC<Pick<RouteProps, 'page'>> = ({ page }) => {
  const { currentPermissionName, isAuthorized } = useSessionStore();

  if (page.permission && !isAuthorized(page.permission)) {
    return <UnauthorizedState />;
  }

  if (
    currentPermissionName &&
    page.requireRole &&
    !page.requireRole.some((role) => role.toLowerCase() === currentPermissionName.toLowerCase())
  ) {
    return <UnauthorizedState />;
  }

  if (!('element' in page) && 'childPages' in page && page.childPages.length > 0) {
    return <PageRenderer page={page.childPages[0]} />;
  }

  return (
    <>
      {page.hasHeaderBorder !== undefined && <PageHeaderBorder mode={page.hasHeaderBorder} />}
      {page.element ? React.createElement(page.element as ElementType) : null}
    </>
  );
};

export const Router = memo(() => {
  const { currentPlatform, currentUser, loginAuthMethod, mfaStatus } = useSessionStore();
  const location = useLocation();
  const navigate = useNavigate();

  const featureFlags = useFeatureFlags();

  const featureFlagRequireLiveVerification = featureFlags[FeatureFlag.EnableLiveModeMfaRequired];
  const allowAppAccess = useMemo(
    () => isAllowedAppAccess(currentPlatform, mfaStatus, featureFlagRequireLiveVerification, loginAuthMethod),
    [currentPlatform, mfaStatus, featureFlagRequireLiveVerification, loginAuthMethod]
  );
  const allRoutes = useMemo(() => {
    return Sitemap.filter((page) => allowAppAccess || page.rules?.allowWithoutPlatformAccess).map((page) => {
      return getSitemapRoute({ page, featureFlags, url: '/' });
    });
  }, [allowAppAccess, featureFlags]);

  useEffect(() => {
    datadogRum.startView({ name: location.pathname });
  }, [location.pathname]);

  useEffect(() => {
    if (!allowAppAccess || currentUser?.platformRoles?.length === 0) {
      // Only redirect if not already on the profile page
      if (!location.pathname.startsWith(ROUTE.PROFILE)) {
        navigate(ROUTE.PROFILE);
      }
    }
  }, [allowAppAccess, currentUser?.platformRoles?.length, location.pathname, navigate]);

  if (!currentUser) {
    return null;
  }

  return <Routes>{allRoutes}</Routes>;
});

export default Router;
